import React, { useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, RowSelectionOptions } from "ag-grid-enterprise";
import { toast } from "react-toastify";
import { Download, Upload } from "lucide-react";

import {
  PrimaryCTAButton,
  SecondaryCTAButton,
} from "../../../components/CTAButtonComponents";
import QuickSearch from "../../../components/QuickSearch";
import {
  companyUsersQueryKey,
  useDeleteCompanyRequest,
  useGetCompanyStats,
  useGetExportExcel,
  useRefreshCompanyStats,
} from "../../../queries/companyStats/captableStats";
import { useUploadStage4Excel } from "../../../queries/dataPipeline";
import { Stage4Excel } from "../../../api/dataPipeline";
import { downloadExcel } from "../../../utils/download";
import ViewUsersCellRenderer, { ViewDetailsCellRenderer } from "./cellRenderer";
import { formatDisplayDate } from "../../../utils/formatDateDisplay";
import { useAuthStore } from "../../../store/authStore";
import { TableData } from "../../../utils/Utility";
import UserAccessPopup from "./userAccessPopup";
import CompanyDetailsPopup from "./CompanyDetailsPopup";
import { DeleteCompanyPopup, EditCompanyPopup } from "./Components";
import BulkUserAccessPopup from "./BulkUserAccessPopup";
import { sideBar } from "../../../utils/AgGridConfig";
import { queryClient } from "../../../queries/client";

type CaptableCompanyDetails = {
  id: string;
  cin: string;
  name: string;
  isPrivate: boolean;
  isClient: boolean;
  noOfShareholders: number;
  noOfRounds: number;
  onPlatformSince: Date;
  dba: string;
  noOfCaptables: number;
  companyAccessData: CompanyAccessData[];
  stage: string;
  stage4Excel: Stage4Excel | null;
};

interface Stage4ExcelCellProps {
  excel: Stage4Excel | null;
  cin: string;
  stage: string;
  isBusy: boolean;
  onUpload: (cin: string, file: File) => void;
}

const isStage4Company = (stage: string) => {
  const normalizedStage = stage
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

  return normalizedStage === "four" || normalizedStage === "stage4";
};

function Stage4ExcelCell({
  excel,
  cin,
  stage,
  isBusy,
  onUpload,
}: Stage4ExcelCellProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isStage4Company(stage)) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onUpload(cin, file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const uploadedDate = excel?.createdAt
    ? new Date(excel.createdAt).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : null;

  return (
    <div className="flex items-center gap-2 py-1">
      {excel ? (
        <a
          href={excel.fileUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs text-emerald-600 border border-emerald-400 rounded hover:bg-emerald-50 max-w-[140px]"
          title={uploadedDate ? `Uploaded ${uploadedDate}` : excel.filename}
          onClick={(e) => e.stopPropagation()}
        >
          <Download className="w-3 h-3 shrink-0" />
          <span className="truncate">{excel.filename}</span>
        </a>
      ) : (
        <span className="text-xs text-gray-400 italic">—</span>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={handleFileChange}
      />
      <button
        disabled={isBusy}
        onClick={(e) => {
          e.stopPropagation();
          fileInputRef.current?.click();
        }}
        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs text-orange-600 border border-orange-400 rounded hover:bg-orange-50 disabled:opacity-50"
        title="Upload Stage-4 Excel"
      >
        <Upload className="w-3 h-3" />
        {isBusy ? "…" : "Upload"}
      </button>
    </div>
  );
}
type CompanyAccessData = {
  id?: number;
  fullName: string;
  email: string;
  createdAt: Date | string;
  role: string;
};

type CaptableStatsGridRow = {
  id: string;
  name: string;
  onPlatformSince: string;
  isPrivate: string;
  isClient: string;
  stage: string;
  noOfShareholders: number;
  noOfRounds: number;
  noOfCaptables: number;
  cin: string;
  companyAccessData: CompanyAccessData[];
  stage4Excel: Stage4Excel | null;
};

const MAX_BULK_COMPANIES = 100;
const BULK_COMPANY_LIMIT_TOAST_ID = "bulk-company-selection-limit";
const normalizeEmail = (value?: string) => value?.trim().toLowerCase() || "";

function CaptableStats() {
  const gridApi = useRef<AgGridReact>(null);
  const { mutate: exportToexcel } = useGetExportExcel();
  const { data: companyStats, refetch, isLoading } = useGetCompanyStats();
  const { mutate: deleteRequest } = useDeleteCompanyRequest();
  const { mutate: uploadStage4 } = useUploadStage4Excel();
  const { mutateAsync: refreshCompanyData } = useRefreshCompanyStats();
  const [uploadingStage4Cin, setUploadingStage4Cin] = useState<string | null>(
    null,
  );
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleStage4Upload = (cin: string, file: File) => {
    const company = companyStats?.find(
      (item: CaptableCompanyDetails) => item.cin === cin,
    );
    if (!company || !isStage4Company(company.stage)) return;

    setUploadingStage4Cin(cin);
    uploadStage4(
      { cin, file },
      {
        onSuccess: () => refetch(),
        onSettled: () => setUploadingStage4Cin(null),
      },
    );
  };
  const [searchResultCompanies, setSearchResultCompanies] = useState(0);
  const [userList, setUserList] = useState<CaptableCompanyDetails>();
  const [open, setOpen] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isCompanyDetailsPopup, setIsCompanyDetailsPopup] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [bulkAccessOpen, setBulkAccessOpen] = useState(false);
  const selectedCompaniesMapRef = useRef<
    Map<string, { id: string; name: string }>
  >(new Map());
  const [selectedCompanyCount, setSelectedCompanyCount] = useState(0);
  const [selectedCompaniesForBulk, setSelectedCompaniesForBulk] = useState<
    { id: string; name: string }[]
  >([]);
  const [isColumnOpen, setIsColumnOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const user = useAuthStore();
  const isSingleRowSelected = selectedCompanyCount === 1;
  const hasSelection = selectedCompanyCount > 0;
  const bulkAccessDisabledReason = !hasSelection
    ? "Select at least 1 company"
    : "";
  const singleSelectionDisabledReason = !hasSelection
    ? "Select at least 1 company"
    : "Select only 1 company";
  const syncSelectedCompaniesState = () => {
    const selectedCompanies = Array.from(
      selectedCompaniesMapRef.current.values(),
    );
    setSelectedCompanyCount(selectedCompanies.length);
    setSelectedCompaniesForBulk(selectedCompanies);
  };

  const refreshCompanyStatsFromServer = async () => {
    const refreshedStats = await refreshCompanyData();
    if (!Array.isArray(refreshedStats)) {
      throw new Error("Invalid company stats response");
    }
    queryClient.setQueryData("getcompanystats", refreshedStats);
  };

  const updateCompanyAccessInClientState = (
    companyId: string,
    updater: (existing: CompanyAccessData[]) => CompanyAccessData[],
  ) => {
    queryClient.setQueryData<CompanyAccessData[]>(
      companyUsersQueryKey(companyId),
      (existing) => updater(Array.isArray(existing) ? existing : []),
    );
    const cachedStats =
      queryClient.getQueryData<CaptableCompanyDetails[]>("getcompanystats");
    if (Array.isArray(cachedStats)) {
      queryClient.setQueryData(
        "getcompanystats",
        cachedStats.map((company) =>
          company.id === companyId
            ? {
                ...company,
                companyAccessData: updater(company.companyAccessData || []),
              }
            : company,
        ),
      );
    }
    setUserList((prev) => {
      if (!prev || prev.id !== companyId) return prev;
      return {
        ...prev,
        companyAccessData: updater(prev.companyAccessData || []),
      };
    });
  };

  const updateCompanyInClientState = (
    companyId: string,
    updates: Partial<CaptableCompanyDetails>,
  ) => {
    queryClient.setQueryData<CaptableCompanyDetails[]>(
      "getcompanystats",
      (existing) =>
        Array.isArray(existing)
          ? existing.map((company) =>
              company.id === companyId ? { ...company, ...updates } : company,
            )
          : [],
    );
    setUserList((existing) =>
      existing?.id === companyId ? { ...existing, ...updates } : existing,
    );
  };

  const handleCompanyDeleted = (companyId: string) => {
    queryClient.setQueryData<CaptableCompanyDetails[]>(
      "getcompanystats",
      (existing) =>
        Array.isArray(existing)
          ? existing.filter((company) => company.id !== companyId)
          : [],
    );
    queryClient.removeQueries(companyUsersQueryKey(companyId), { exact: true });
    selectedCompaniesMapRef.current.delete(companyId);
    syncSelectedCompaniesState();
    setUserList((existing) =>
      existing?.id === companyId ? undefined : existing,
    );
  };

  const invalidateCompanyUsers = (companyIds: string[]) => {
    companyIds.forEach((companyId) => {
      queryClient.invalidateQueries(companyUsersQueryKey(companyId), {
        exact: true,
      });
    });
  };

  const handleUserAccessAdded = (companyId: string, access: TableData) => {
    const normalizedEmail = normalizeEmail(access.email);
    if (!normalizedEmail) return;
    const accessToUpsert: CompanyAccessData = {
      id: access.id,
      email: normalizedEmail,
      fullName: access.fullName || normalizedEmail.split("@")[0] || "",
      role: access.role || "Admin",
      createdAt: access.createdAt || new Date().toISOString(),
    };
    updateCompanyAccessInClientState(companyId, (existing) => {
      const next = existing.filter((row) => {
        if (
          accessToUpsert.id !== undefined &&
          row.id !== undefined &&
          row.id !== null
        ) {
          return row.id !== accessToUpsert.id;
        }
        return normalizeEmail(row.email) !== normalizedEmail;
      });
      next.push(accessToUpsert);
      return next;
    });
  };

  const handleUserAccessRemoved = (
    companyId: string,
    target: { id?: number; email?: string },
  ) => {
    const targetEmail = normalizeEmail(target.email);
    updateCompanyAccessInClientState(companyId, (existing) =>
      existing.filter((row) => {
        if (
          target.id !== undefined &&
          row.id !== undefined &&
          row.id !== null
        ) {
          return row.id !== target.id;
        }
        if (targetEmail) {
          return normalizeEmail(row.email) !== targetEmail;
        }
        return true;
      }),
    );
  };

  const handleExcelDownload = () => {
    exportToexcel(companyStats, {
      onSuccess: (res) => {
        const fileName = `Company Details Report`;
        downloadExcel(res, fileName);
      },
      onError: () => {},
    });
  };
  const handleDeleteRequest = () => {
    const row: any = gridApi.current?.api.getSelectedRows();
    if (!row || row.length !== 1) {
      toast("Select exactly one company to delete", {
        type: "error",
        autoClose: 2000,
      });
      return;
    }
    setOpen(true);
    deleteRequest(row, {
      onSuccess: (res: any) => {
        if (res !== undefined) {
          toast("OTP Generated Succesfully", {
            type: "success",
            autoClose: 2000,
          });
        }
      },
      onError: (error: any) => {
        toast(error.response.data.errors, {
          type: "error",
          autoClose: 2000,
        });
      },
    });
  };
  const handleOpenBulkAccess = () => {
    if (!hasSelection) {
      toast("Select at least one company", {
        type: "error",
        autoClose: 2000,
      });
      return;
    }
    if (selectedCompanyCount > MAX_BULK_COMPANIES) {
      toast(`You can select a maximum of ${MAX_BULK_COMPANIES} companies`, {
        type: "error",
        autoClose: 2500,
      });
      return;
    }
    setBulkAccessOpen(true);
  };
  const handleRemoveCompanyFromSelection = (companyId: string) => {
    const selectedCompaniesMap = selectedCompaniesMapRef.current;
    if (selectedCompaniesMap.has(companyId)) {
      selectedCompaniesMap.delete(companyId);
      syncSelectedCompaniesState();
    }
    const rowNode = gridApi.current?.api.getRowNode(companyId);
    if (rowNode?.isSelected()) rowNode.setSelected(false);
  };
  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      filter: true,
      minWidth: 150,
      flex: 1,
    }),
    [],
  );
  const rowSelection = useMemo<RowSelectionOptions>(() => {
    return {
      mode: "multiRow",
      checkboxes: true,
      headerCheckbox: true,
      selectAll: "currentPage",
    };
  }, []);
  const columnDefs: ColDef[] = [
    {
      headerName: "Company ID",
      field: "id",
      sortable: true,
      hide: true,
    },
    {
      headerName: "Name",
      field: "name",
      sortable: true,
    },
    {
      headerName: "On Platform Since",
      field: "onPlatformSince",
      sortable: true,
    },
    {
      headerName: "Private Company",
      field: "isPrivate",
      sortable: true,
    },
    {
      headerName: "Client Company",
      field: "isClient",
      sortable: true,
    },
    {
      headerName: "Stage",
      field: "stage",
      sortable: true,
    },
    {
      headerName: "View Users",
      field: "userEmails",
      cellRenderer: ViewUsersCellRenderer,
      onCellClicked: (e) => {
        setUserList(e.data);
        setIsPopupOpen(true);
      },
    },
    {
      headerName: "View Details",
      cellRenderer: ViewDetailsCellRenderer,
      onCellClicked: (e) => {
        setUserList(e.data);
        setIsCompanyDetailsPopup(true);
      },
    },
    {
      headerName: "Shareholders",
      field: "noOfShareholders",
      sortable: true,
    },
    {
      headerName: "Rounds",
      field: "noOfRounds",
      sortable: true,
    },

    {
      headerName: "Captables",
      field: "noOfCaptables",
      sortable: true,
    },

    {
      headerName: "CIN",
      field: "cin",
      sortable: true,
    },
    {
      headerName: "Stage 4 Excel",
      field: "stage4Excel",
      minWidth: 220,
      cellRenderer: (params: any) => (
        <Stage4ExcelCell
          excel={params.data?.stage4Excel ?? null}
          cin={params.data?.cin ?? ""}
          stage={params.data?.stage ?? ""}
          isBusy={uploadingStage4Cin === params.data?.cin}
          onUpload={handleStage4Upload}
        />
      ),
    },
  ];
  const rowsData = useMemo(
    () =>
      companyStats?.map((company: CaptableCompanyDetails) => ({
        name: company.name,
        onPlatformSince: formatDisplayDate(company.onPlatformSince),
        isPrivate: company.isPrivate ? "Yes" : "No",
        isClient: company.isClient ? "Yes" : "No",
        stage: company.stage,
        noOfShareholders: company.noOfShareholders,
        noOfRounds: company.noOfRounds,
        noOfCaptables: company.noOfCaptables,
        cin: company.cin,
        companyAccessData: company.companyAccessData,
        id: company.id,
        stage4Excel: company.stage4Excel ?? null,
      })),
    [companyStats],
  );
  useEffect(() => {
    setSearchResultCompanies(
      gridApi?.current?.api?.getDisplayedRowCount() || 0,
    );
  }, [rowsData]);
  useEffect(() => {
    const rowIds = new Set(
      (rowsData || []).map((row: CaptableStatsGridRow) => row.id),
    );
    const selectedCompaniesMap = selectedCompaniesMapRef.current;
    let hasSelectionChanges = false;

    selectedCompaniesMap.forEach((_, companyId) => {
      if (!rowIds.has(companyId)) {
        selectedCompaniesMap.delete(companyId);
        hasSelectionChanges = true;
      }
    });

    if (hasSelectionChanges) syncSelectedCompaniesState();
  }, [rowsData]);
  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await refreshCompanyStatsFromServer();
      queryClient.invalidateQueries("companyUsers");
    } catch (error) {
      toast("Could not refresh stats from server", {
        type: "warning",
        autoClose: 2000,
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const openToolPanel = (key: any) => {
    if (key === "columns") {
      if (gridApi) {
        if (!isColumnOpen) gridApi?.current?.api?.openToolPanel(key);
        else gridApi?.current?.api?.closeToolPanel();
        setIsColumnOpen((state) => !state);
        setIsFilterOpen(false);
      }
    } else if (key === "filters") {
      if (gridApi) {
        if (!isFilterOpen) {
          gridApi?.current?.api?.openToolPanel(key);
        } else gridApi?.current?.api?.closeToolPanel();
        setIsFilterOpen((state) => !state);
        setIsColumnOpen(false);
      }
    }
  };
  return (
    <>
      <div className="flex justify-between">
        <div>
          <h1 className="text-2xl font-medium">Captable Companies</h1>
          <p className="text-sm text-gray-500 font-medium pb-2">
            {searchResultCompanies} companies
            <span className="mx-2 text-gray-400">|</span>
            <span
              className={
                selectedCompanyCount > 0 ? "text-primary font-semibold" : ""
              }
            >
              {selectedCompanyCount} selected
            </span>
          </p>
        </div>
        <div className="flex py-2">
          {(user.role === "Admin" || user.role === "SuperAdmin") && (
            <span
              title={!hasSelection ? bulkAccessDisabledReason : undefined}
              className="relative inline-flex me-2 my-1 group"
            >
              <SecondaryCTAButton
                onClick={handleOpenBulkAccess}
                className={
                  hasSelection
                    ? "cursor-pointer"
                    : "cursor-not-allowed pointer-events-none"
                }
                disabled={!hasSelection}
              >
                Bulk Access
              </SecondaryCTAButton>
              {!hasSelection && (
                <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-1 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow transition-opacity duration-150 group-hover:opacity-100">
                  {bulkAccessDisabledReason}
                </span>
              )}
            </span>
          )}
          {user.role === "SuperAdmin" && (
            <>
              <span
                title={
                  !isSingleRowSelected
                    ? singleSelectionDisabledReason
                    : undefined
                }
                className="relative inline-flex me-2 my-1 group"
              >
                <SecondaryCTAButton
                  onClick={() => {
                    setEditOpen(true);
                  }}
                  className={
                    isSingleRowSelected
                      ? "cursor-pointer"
                      : "cursor-not-allowed pointer-events-none"
                  }
                  disabled={!isSingleRowSelected}
                >
                  Edit
                </SecondaryCTAButton>
                {!isSingleRowSelected && (
                  <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-1 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow transition-opacity duration-150 group-hover:opacity-100">
                    {singleSelectionDisabledReason}
                  </span>
                )}
              </span>
              <span
                title={
                  !isSingleRowSelected
                    ? singleSelectionDisabledReason
                    : undefined
                }
                className="relative inline-flex me-2 my-1 group"
              >
                <SecondaryCTAButton
                  onClick={() => {
                    handleDeleteRequest();
                  }}
                  className={
                    isSingleRowSelected
                      ? "cursor-pointer"
                      : "cursor-not-allowed pointer-events-none"
                  }
                  disabled={!isSingleRowSelected}
                >
                  Delete
                </SecondaryCTAButton>
                {!isSingleRowSelected && (
                  <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-1 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow transition-opacity duration-150 group-hover:opacity-100">
                    {singleSelectionDisabledReason}
                  </span>
                )}
              </span>
            </>
          )}
          <QuickSearch
            placeholder="Search"
            onChange={(e) => {
              gridApi.current!.api.setGridOption(
                "quickFilterText",
                e.target.value,
              );
              setSearchResultCompanies(
                gridApi.current?.api.getDisplayedRowCount() || 0,
              );
            }}
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 py-2">
        <PrimaryCTAButton onClick={handleRefresh} disabled={isRefreshing}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            fill="currentColor"
            className="size-4 mr-1"
          >
            <path
              fillRule="evenodd"
              d="M8 2a6 6 0 1 0 6 6 1 1 0 0 1 2 0 8 8 0 1 1-8-8 1 1 0 0 1 0 2zm3.5-2a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h3a1 1 0 1 0 0-2h-2V1a1 1 0 0 0-1-1z"
              clipRule="evenodd"
            />
          </svg>
          Refresh
        </PrimaryCTAButton>
        <PrimaryCTAButton
          onClick={() => {
            handleExcelDownload();
          }}
          disabled={isLoading}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            fill="currentColor"
            className="size-4"
          >
            <path
              fillRule="evenodd"
              d="M15 11a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v6ZM7.25 7.5a.5.5 0 0 0-.5-.5H3a.5.5 0 0 0-.5.5V8a.5.5 0 0 0 .5.5h3.75a.5.5 0 0 0 .5-.5v-.5Zm1.5 3a.5.5 0 0 1 .5-.5H13a.5.5 0 0 1 .5.5v.5a.5.5 0 0 1-.5.5H9.25a.5.5 0 0 1-.5-.5v-.5ZM13.5 8v-.5A.5.5 0 0 0 13 7H9.25a.5.5 0 0 0-.5.5V8a.5.5 0 0 0 .5.5H13a.5.5 0 0 0 .5-.5Zm-6.75 3.5a.5.5 0 0 0 .5-.5v-.5a.5.5 0 0 0-.5-.5H3a.5.5 0 0 0-.5.5v.5a.5.5 0 0 0 .5.5h3.75Z"
              clipRule="evenodd"
            />
          </svg>
          Export To Excel
        </PrimaryCTAButton>
        <PrimaryCTAButton
          onClick={() => {
            openToolPanel("columns");
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            fill="currentColor"
            className="size-4 me-1"
          >
            <path d="M9.836 3h-3.67v10h3.67V3ZM11.336 13H13.5a1.5 1.5 0 0 0 1.5-1.5v-7A1.5 1.5 0 0 0 13.5 3h-2.164v10ZM2.5 3h2.166v10H2.5A1.5 1.5 0 0 1 1 11.5v-7A1.5 1.5 0 0 1 2.5 3Z" />
          </svg>
          Columns
        </PrimaryCTAButton>
        <PrimaryCTAButton
          onClick={() => {
            openToolPanel("filters");
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            fill="currentColor"
            className="size-4 me-1"
          >
            <path d="M14 2a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v2.172a2 2 0 0 0 .586 1.414l2.828 2.828A2 2 0 0 1 6 9.828v4.363a.5.5 0 0 0 .724.447l2.17-1.085A2 2 0 0 0 10 11.763V9.829a2 2 0 0 1 .586-1.414l2.828-2.828A2 2 0 0 0 14 4.172V2Z" />
          </svg>
          Filter
        </PrimaryCTAButton>
      </div>
      <div className={`ag-theme-quartz h-[calc(100vh-28vh)]`}>
        <AgGridReact
          ref={gridApi}
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          rowData={rowsData}
          getRowId={(params) => params.data.id}
          suppressRowTransform={true}
          pagination={true}
          animateRows={true}
          rowSelection={rowSelection}
          sideBar={sideBar}
          onModelUpdated={() => {
            setSearchResultCompanies(
              gridApi.current?.api.getDisplayedRowCount() || 0,
            );
          }}
          onRowSelected={(params: any) => {
            const row = params.data as CaptableStatsGridRow | undefined;
            if (!row?.id) return;
            const selectedCompaniesMap = selectedCompaniesMapRef.current;

            if (params.node.isSelected()) {
              if (
                !selectedCompaniesMap.has(row.id) &&
                selectedCompaniesMap.size >= MAX_BULK_COMPANIES
              ) {
                params.node.setSelected(false);
                if (!toast.isActive(BULK_COMPANY_LIMIT_TOAST_ID)) {
                  toast(
                    `Maximum ${MAX_BULK_COMPANIES} companies can be selected for bulk access`,
                    {
                      type: "error",
                      autoClose: 2500,
                      toastId: BULK_COMPANY_LIMIT_TOAST_ID,
                    },
                  );
                }
                return;
              }
              selectedCompaniesMap.set(row.id, { id: row.id, name: row.name });
            } else {
              selectedCompaniesMap.delete(row.id);
            }
            syncSelectedCompaniesState();
          }}
          getRowStyle={(params) => {
            if (params.rowIndex % 2 === 0) {
              return { background: "#f8f8f8" };
            } else {
              return { background: "#ffffff" };
            }
          }}
        />
      </div>
      {isPopupOpen && (
        <UserAccessPopup
          isPopupOpen
          userList={userList}
          onUserAdded={handleUserAccessAdded}
          onUserRemoved={handleUserAccessRemoved}
          onClose={() => {
            setIsPopupOpen(false);
          }}
        />
      )}
      {isCompanyDetailsPopup && (
        <CompanyDetailsPopup
          isCompanyDetailsPopup
          userList={userList}
          onClose={() => {
            setIsCompanyDetailsPopup(false);
          }}
        />
      )}
      {editOpen && (
        <EditCompanyPopup
          showPopup={editOpen}
          gridRef={gridApi}
          onClose={() => {
            setEditOpen(false);
          }}
          onCompanyEdited={(companyId, updates) =>
            updateCompanyInClientState(companyId, updates)
          }
        />
      )}
      {open && (
        <DeleteCompanyPopup
          showPopup={open}
          onClose={() => {
            setOpen(false);
          }}
          gridRef={gridApi}
          onCompanyDeleted={handleCompanyDeleted}
        />
      )}
      {bulkAccessOpen && (
        <BulkUserAccessPopup
          isOpen={bulkAccessOpen}
          onClose={() => {
            setBulkAccessOpen(false);
          }}
          selectedCompanies={selectedCompaniesForBulk}
          onRemoveCompany={handleRemoveCompanyFromSelection}
          onAccessGranted={invalidateCompanyUsers}
        />
      )}
    </>
  );
}

export default CaptableStats;
